/**
 * Application database layer — backed by SQLite WASM.
 *
 * Replaces the old Dexie (IndexedDB) implementation with SQLite for
 * cross-platform consistency (same engine as D1 on the server).
 *
 * Exports the same interface used by hooks and components to minimize
 * changes throughout the codebase. The main difference is that reactive
 * queries now use useSQLiteQuery instead of useLiveQuery.
 */

import { normalizeTagIds, noteTitleForDisplay } from "@caliu/utils";
import type { Note, SyncOperation, SyncStatus, Tag } from "../types";
import {
  projectNoteAttachments,
  type AttachmentProjectionNoteInput,
} from "./attachmentProjection";
import { rowToLocalNote, rowToLocalTag } from "./repositories";
import type { NoteRow, TagRow, SyncOpRow } from "./repositories";
import {
  clearAllData,
  execute,
  initSqlite,
  isDbReady,
  notifyChanges,
  persist,
  query,
  queryOne,
  transaction,
} from "./sqlite";
import { scheduleSyncFlush } from "./syncScheduler";
import { hasUnsentWork, loadSyncState, readEngineRow, recordLocalEdit, refusesLocalEdit, type EngineRow } from "./noteEngineState";
import i18next from "i18next";

// ── Re-exports for convenience ─────────────────────────────────────────────

export { isDbReady, persist };
export type { SyncOperation, SyncStatus };

// ── Types ──────────────────────────────────────────────────────────────────

export interface LocalNote extends Note {
  syncStatus: SyncStatus;
  /**
   * True when this row is a read-only stub for a note shared with the user
   * by someone else (`shared_note_refs` + `shared_notes` cache), not a row
   * in the local `notes` table. Stubs surface in All Notes with a limited
   * action set and route to the shared-note viewer instead of the editor.
   */
  sharedWithMe?: boolean;
  /** Display name of the sharer, from the shared_notes cache. */
  sharedAuthorName?: string;
  /**
   * Sharing (PLAN.md "Sharing data", Sync), from pulled rows; absent on an
   * unshared note of this account. `ownerId` names another account on a note
   * shared with this one.
   */
  ownerId?: string;
  ownerName?: string;
  role?: "owner" | "editor" | "viewer";
  memberCount?: number;
  linkAccess?: "none" | "view" | "edit";
}

export interface LocalTag extends Tag {
  syncStatus: SyncStatus;
}

export interface LocalNoteLink {
  sourceNoteId: string;
  targetNoteId: string;
}

export interface MetaEntry {
  key: string;
  value: string;
}

// ── Row mappers ────────────────────────────────────────────────────────────

// Note and Tag row mappers are centralized in lib/repositories/.
// Use rowToLocalNote and rowToLocalTag imported above.

function rowToSyncOp(row: SyncOpRow): SyncOperation {
  return {
    id: row.id,
    type: row.type as SyncOperation["type"],
    entity: row.entity as SyncOperation["entity"],
    entityId: row.entity_id,
    data: row.data ? JSON.parse(row.data) : undefined,
    timestamp: row.timestamp,
    retries: row.retries,
  };
}

function replaceAttachmentProjectionForNote(
  note: AttachmentProjectionNoteInput,
): void {
  execute("DELETE FROM attachment_projection WHERE note_id = ?", [note.id]);

  for (const attachment of projectNoteAttachments(note)) {
    execute(
      `INSERT OR REPLACE INTO attachment_projection
       (note_id, position, user_id, kind, alt, src, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        attachment.noteId,
        attachment.position,
        attachment.userId,
        attachment.kind,
        attachment.alt,
        attachment.src,
        attachment.createdAt,
        attachment.updatedAt,
      ],
    );
  }
}

export { replaceAttachmentProjectionForNote };

// ── Init ───────────────────────────────────────────────────────────────────

export async function initDB(): Promise<void> {
  await initSqlite();
  rebuildNoteLinksIfNeeded();
}

export async function clearDatabase(): Promise<void> {
  clearAllData();
  await persist();
}

// ── Notes ──────────────────────────────────────────────────────────────────

export function getAllNotes(): LocalNote[] {
  const rows = query<NoteRow>("SELECT * FROM notes ORDER BY updated_at DESC");
  return rows.map(rowToLocalNote);
}

export function getNotesByTag(tagId: string): LocalNote[] {
  const rows = query<NoteRow>(
    `SELECT n.* FROM notes n
     JOIN note_tags nt ON nt.note_id = n.id
     WHERE nt.tag_id = ?
     ORDER BY n.updated_at DESC`,
    [tagId],
  );
  return rows.map(rowToLocalNote);
}

/**
 * What a local write sets `sync_status` to. A note in `conflict` STAYS in
 * conflict: marking it `pending` took it out of the conflict screen and the
 * next push sent the local text over the server's version, a "keep mine"
 * nobody chose. The write itself still lands (text, pin, trash) and its op is
 * queued; the push holds that op until the conflict is resolved (see
 * `SyncService.pushToServer`).
 */
const PENDING_UNLESS_CONFLICT =
  "CASE WHEN sync_status = 'conflict' THEN 'conflict' ELSE 'pending' END";

export function getNote(id: string): LocalNote | undefined {
  const row = queryOne<NoteRow>("SELECT * FROM notes WHERE id = ?", [id]);
  if (!row) return undefined;
  return rowToLocalNote(row);
}

export function putNote(note: LocalNote): void {
  transaction(() => {
    // Preserve existing user_id if not provided (server responses omit it)
    const existing = queryOne<{
      user_id: string;
      title: string;
      content: string;
      word_count: number;
      base_content: string | null;
      rev: number;
      base_updated_at: string;
      inflight_client_id: string | null;
      inflight_seq: number | null;
      inflight_base_rev: number | null;
      inflight_ops: string | null;
      pending_ops: string | null;
      owner_id: string | null;
      owner_name: string | null;
      share_role: string | null;
      member_count: number | null;
      link_access: string | null;
    }>(
      `SELECT user_id, title, content, word_count, base_content, rev, base_updated_at,
         inflight_client_id, inflight_seq, inflight_base_rev, inflight_ops, pending_ops,
         owner_id, owner_name, share_role, member_count, link_access
       FROM notes WHERE id = ?`,
      [note.id],
    );
    // Caller rule 1 (PLAN.md "Client engine"): a text change of a note in the
    // content sync engine goes through localEdit, before the row is replaced.
    // The engine's columns are then read again and carried over: a REPLACE
    // would reset them, and a lost change in flight would make its own echo
    // look foreign (doubled text).
    let engine: Pick<EngineRow, "inflight_client_id" | "inflight_seq" | "inflight_base_rev" | "inflight_ops" | "pending_ops"> | null = existing;
    if (existing && note.content !== undefined && note.content !== existing.content) {
      const refused =
        ((note.syncStatus ?? "synced") !== "synced" && refusesLocalEdit(note.id)) ||
        !recordLocalEdit(note.id, note.content);
      // A viewer's note keeps its text (PLAN.md D5); the rest of the write applies.
      if (refused) note = { ...note, title: existing.title, content: existing.content, wordCount: existing.word_count };
      engine = readEngineRow(note.id);
    }
    // Preserve existing user_id if not provided (server responses omit it)
    const existingUserId = note.userId || existing?.user_id || "";
    // A 'synced' put means this content is now the last-synced state → it
    // becomes the merge ancestor. A 'pending'/'conflict' put is a local edit
    // (or a brand-new note) — keep the prior base (NULL for new notes). Since
    // this is INSERT OR REPLACE (full-row), base_content must be carried
    // explicitly or the REPLACE would wipe it to NULL. A note in the content
    // engine keeps its base whatever the status: only the engine moves it.
    const inEngine = engine?.pending_ops != null;
    const baseContent =
      !inEngine && (note.syncStatus ?? "synced") === "synced"
        ? (note.content ?? "")
        : (existing?.base_content ?? null);
    // Outside the engine a new base has no known rev.
    const rev = inEngine || baseContent === (existing?.base_content ?? null) ? (existing?.rev ?? 0) : 0;
    execute(
      `INSERT OR REPLACE INTO notes (id, user_id, title, content, is_pinned, is_encrypted, encryption_hint, word_count, created_at, updated_at, deleted_at, archived_at, shared_at, sort_order, base_content, is_read_only, sync_status,
         rev, base_updated_at, inflight_client_id, inflight_seq, inflight_base_rev, inflight_ops, pending_ops,
         owner_id, owner_name, share_role, member_count, link_access)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        note.id,
        existingUserId,
        note.title ?? "",
        note.content ?? "",
        note.isPinned ? 1 : 0,
        note.isEncrypted ? 1 : 0,
        note.encryptionHint ?? null,
        note.wordCount ?? 0,
        note.createdAt ?? new Date().toISOString(),
        note.updatedAt ?? new Date().toISOString(),
        note.deletedAt ?? null,
        note.archivedAt ?? null,
        note.sharedAt ?? null,
        note.sortOrder ?? null,
        baseContent,
        note.isReadOnly ? 1 : 0,
        note.syncStatus ?? "synced",
        rev,
        existing?.base_updated_at ?? "",
        engine?.inflight_client_id ?? null,
        engine?.inflight_seq ?? null,
        engine?.inflight_base_rev ?? null,
        engine?.inflight_ops ?? null,
        engine?.pending_ops ?? null,
        // Sharing comes from pulls: a local write carries it over (REPLACE
        // would reset it) unless the caller passes its own.
        note.ownerId ?? existing?.owner_id ?? null,
        note.ownerName ?? existing?.owner_name ?? null,
        note.role ?? existing?.share_role ?? null,
        note.memberCount ?? existing?.member_count ?? 0,
        note.linkAccess ?? existing?.link_access ?? null,
      ],
    );

    // Sync note_tags
    execute("DELETE FROM note_tags WHERE note_id = ?", [note.id]);
    for (const tagId of note.tags) {
      execute(
        "INSERT OR IGNORE INTO note_tags (note_id, tag_id) VALUES (?, ?)",
        [note.id, tagId],
      );
    }

    replaceAttachmentProjectionForNote({
      id: note.id,
      userId: existingUserId,
      content: note.content ?? "",
      createdAt: note.createdAt ?? new Date().toISOString(),
      updatedAt: note.updatedAt ?? new Date().toISOString(),
    });
  });
  notifyChanges([
    "notes",
    "note_tags",
    "attachment_projection",
  ]);
}

export function updateNote(id: string, updates: Partial<LocalNote>): void {
  if (updates.content !== undefined && updates.syncStatus !== "synced" && refusesLocalEdit(id)) {
    // A viewer's note takes no local text (PLAN.md D5): the rest still applies.
    const { content: _content, title: _title, wordCount: _wordCount, ...rest } = updates;
    updates = rest;
  }
  const setClauses: string[] = [];
  const params: unknown[] = [];

  if (updates.title !== undefined) {
    setClauses.push("title = ?");
    params.push(updates.title);
  }
  if (updates.content !== undefined) {
    setClauses.push("content = ?");
    params.push(updates.content);
  }
  if (updates.isPinned !== undefined) {
    setClauses.push("is_pinned = ?");
    params.push(updates.isPinned ? 1 : 0);
  }
  if (updates.wordCount !== undefined) {
    setClauses.push("word_count = ?");
    params.push(updates.wordCount);
  }
  if (updates.updatedAt !== undefined) {
    setClauses.push("updated_at = ?");
    params.push(updates.updatedAt);
  }
  if (updates.deletedAt !== undefined) {
    setClauses.push("deleted_at = ?");
    params.push(updates.deletedAt);
  }
  if ("deletedAt" in updates && updates.deletedAt === undefined) {
    setClauses.push("deleted_at = NULL");
  }
  if (updates.archivedAt !== undefined) {
    setClauses.push("archived_at = ?");
    params.push(updates.archivedAt);
  }
  if ("archivedAt" in updates && updates.archivedAt === undefined) {
    setClauses.push("archived_at = NULL");
  }
  if (updates.syncStatus === "pending") {
    setClauses.push(`sync_status = ${PENDING_UNLESS_CONFLICT}`);
  } else if (updates.syncStatus !== undefined) {
    setClauses.push("sync_status = ?");
    params.push(updates.syncStatus);
  }
  if (updates.sharedAt !== undefined) {
    setClauses.push("shared_at = ?");
    params.push(updates.sharedAt);
  }
  if (updates.sortOrder !== undefined && updates.sortOrder !== null) {
    setClauses.push("sort_order = ?");
    params.push(updates.sortOrder);
  }
  if ("sortOrder" in updates && updates.sortOrder === null) {
    setClauses.push("sort_order = NULL");
  }
  if (updates.isReadOnly !== undefined) {
    setClauses.push("is_read_only = ?");
    params.push(updates.isReadOnly ? 1 : 0);
  }

  if (setClauses.length === 0) return;

  transaction(() => {
    // Caller rule 1 (PLAN.md "Client engine"): before the text is written.
    if (updates.content !== undefined && !recordLocalEdit(id, updates.content)) return;
    params.push(id);
    execute(`UPDATE notes SET ${setClauses.join(", ")} WHERE id = ?`, params);

    if (updates.tags !== undefined) {
      execute("DELETE FROM note_tags WHERE note_id = ?", [id]);
      for (const tagId of updates.tags) {
        execute(
          "INSERT OR IGNORE INTO note_tags (note_id, tag_id) VALUES (?, ?)",
          [id, tagId],
        );
      }
    }

    if (updates.content !== undefined) {
      const note = queryOne<{
        id: string;
        user_id: string;
        content: string;
        created_at: string;
        updated_at: string;
      }>(
        `SELECT id, user_id, content, created_at, updated_at
         FROM notes
         WHERE id = ?`,
        [id],
      );
      if (note) {
        replaceAttachmentProjectionForNote({
          id: note.id,
          userId: note.user_id,
          content: note.content,
          createdAt: note.created_at,
          updatedAt: note.updated_at,
        });
      }
    }
  });

  const changedTables = ["notes"];
  if (updates.tags !== undefined) changedTables.push("note_tags");
  if (updates.content !== undefined) {
    changedTables.push("attachment_projection");
  }
  notifyChanges(changedTables);
}

export function deleteNoteLocal(id: string): void {
  transaction(() => {
    execute("DELETE FROM note_tags WHERE note_id = ?", [id]);
    execute(
      "DELETE FROM note_links WHERE source_note_id = ? OR target_note_id = ?",
      [id, id],
    );
    execute("DELETE FROM notes WHERE id = ?", [id]);
  });
  notifyChanges([
    "notes",
    "note_tags",
    "note_links",
    "attachment_projection",
  ]);
}

export function softDeleteNote(id: string): void {
  transaction(() => {
    // Clear archived_at too: trash and archive are mutually exclusive, so a
    // deleted archived note restores to Active (both fields null), not Archive.
    // The 'soft-delete' sync op carries no payload — the server nulls
    // archived_at on its side, mirroring this local write.
    execute(
      `UPDATE notes SET deleted_at = ?, archived_at = NULL, sync_status = ${PENDING_UNLESS_CONFLICT} WHERE id = ?`,
      [new Date().toISOString(), id],
    );
    // Remove note_tags so orphan cleanup can reclaim tags only used by this note
    execute("DELETE FROM note_tags WHERE note_id = ?", [id]);
    // Orphan cleanup: remove tags not used by any non-deleted note and not a parent of another tag
    execute(`
      DELETE FROM tags WHERE id IN (
        SELECT t.id FROM tags t
        WHERE t.id NOT IN (SELECT DISTINCT tag_id FROM note_tags)
          AND t.id NOT IN (SELECT DISTINCT parent_id FROM tags WHERE parent_id IS NOT NULL)
      )
    `);
  });
  notifyChanges(["notes", "note_tags", "tags"]);
}

export function restoreNote(id: string): void {
  execute(
    `UPDATE notes SET deleted_at = NULL, sync_status = ${PENDING_UNLESS_CONFLICT} WHERE id = ?`,
    [id],
  );
  notifyChanges(["notes"]);
}

export function archiveNote(id: string): void {
  // Unlike softDeleteNote, archive does NOT strip note_tags or orphan-clean:
  // archived notes keep their folder membership so unarchive is lossless. They
  // are still hidden from tag/folder views by the query filters.
  execute(
    `UPDATE notes SET archived_at = ?, sync_status = ${PENDING_UNLESS_CONFLICT} WHERE id = ?`,
    [new Date().toISOString(), id],
  );
  notifyChanges(["notes"]);
}

export function unarchiveNote(id: string): void {
  execute(
    `UPDATE notes SET archived_at = NULL, sync_status = ${PENDING_UNLESS_CONFLICT} WHERE id = ?`,
    [id],
  );
  notifyChanges(["notes"]);
}

export function upsertNotes(notes: Note[]): void {
  transaction(() => {
    for (const n of notes) {
      // Check if note exists and has pending changes
      const existing = queryOne<NoteRow & { pending_ops: string | null }>(
        "SELECT sync_status, updated_at, user_id, pending_ops FROM notes WHERE id = ?",
        [n.id],
      );

      // Never overwrite a note that has unsaved local changes
      if (existing?.sync_status === "pending") continue;
      // Nor one in the content engine: its text comes through /sync/content,
      // and the REPLACE would drop its engine columns. (No caller today; kept
      // because SyncStorage declares it.)
      if (existing && existing.pending_ops != null) continue;

      // Only overwrite if server version is newer
      if (existing && n.updatedAt && existing.updated_at) {
        if (
          new Date(n.updatedAt).getTime() <
          new Date(existing.updated_at).getTime()
        )
          continue;
      }

      execute(
        `INSERT OR REPLACE INTO notes (id, user_id, title, content, is_pinned, is_encrypted, encryption_hint, word_count, created_at, updated_at, deleted_at, archived_at, shared_at, sort_order, is_read_only, sync_status)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'synced')`,
        [
          n.id,
          existing?.user_id ?? n.userId ?? "",
          n.title ?? "",
          n.content ?? "",
          n.isPinned ? 1 : 0,
          n.isEncrypted ? 1 : 0,
          n.encryptionHint ?? null,
          n.wordCount ?? 0,
          n.createdAt ?? new Date().toISOString(),
          n.updatedAt ?? new Date().toISOString(),
          n.deletedAt ?? null,
          n.archivedAt ?? null,
          n.sharedAt ?? null,
          n.sortOrder ?? null,
          n.isReadOnly ? 1 : 0,
        ],
      );

      // Update tags if server provides them
      const serverTags =
        Array.isArray(n.tags) && n.tags.length > 0
          ? normalizeTagIds(n.tags)
          : null;
      if (serverTags) {
        execute("DELETE FROM note_tags WHERE note_id = ?", [n.id]);
        for (const tagId of serverTags) {
          execute(
            "INSERT OR IGNORE INTO note_tags (note_id, tag_id) VALUES (?, ?)",
            [n.id, tagId],
          );
        }
      }

      replaceAttachmentProjectionForNote({
        id: n.id,
        userId: existing?.user_id ?? n.userId ?? "",
        content: n.content ?? "",
        createdAt: n.createdAt ?? new Date().toISOString(),
        updatedAt: n.updatedAt ?? new Date().toISOString(),
      });
    }
  });
  notifyChanges([
    "notes",
    "note_tags",
    "attachment_projection",
  ]);
}

// ── Tags ───────────────────────────────────────────────────────────────────

export function getAllTags(): LocalTag[] {
  const rows = query<TagRow>("SELECT * FROM tags ORDER BY name");
  return rows.map(rowToLocalTag);
}

export function getTag(id: string): LocalTag | undefined {
  const row = queryOne<TagRow>("SELECT * FROM tags WHERE id = ?", [id]);
  return row ? rowToLocalTag(row) : undefined;
}

export function putTag(tag: LocalTag): void {
  execute(
    `INSERT OR REPLACE INTO tags (id, user_id, name, parent_id, color, icon, is_pinned, created_at, sync_status)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      tag.id,
      tag.userId ?? "",
      tag.name,
      tag.parentId ?? null,
      tag.color ?? null,
      tag.icon ?? null,
      tag.isPinned ? 1 : 0,
      tag.createdAt ?? new Date().toISOString(),
      tag.syncStatus ?? "synced",
    ],
  );
  notifyChanges(["tags"]);
}

export function updateTag(id: string, updates: Partial<LocalTag>): void {
  const setClauses: string[] = [];
  const params: unknown[] = [];

  if (updates.name !== undefined) {
    setClauses.push("name = ?");
    params.push(updates.name);
  }
  if (updates.color !== undefined) {
    setClauses.push("color = ?");
    params.push(updates.color);
  }
  if (updates.icon !== undefined) {
    setClauses.push("icon = ?");
    params.push(updates.icon);
  }
  if (updates.isPinned !== undefined) {
    setClauses.push("is_pinned = ?");
    params.push(updates.isPinned ? 1 : 0);
  }
  if (updates.parentId !== undefined) {
    setClauses.push("parent_id = ?");
    params.push(updates.parentId);
  }
  if (updates.syncStatus !== undefined) {
    setClauses.push("sync_status = ?");
    params.push(updates.syncStatus);
  }

  if (setClauses.length === 0) return;

  params.push(id);
  execute(`UPDATE tags SET ${setClauses.join(", ")} WHERE id = ?`, params);
  notifyChanges(["tags"]);
}

export function deleteTagLocal(id: string): void {
  transaction(() => {
    execute("DELETE FROM note_tags WHERE tag_id = ?", [id]);
    execute("DELETE FROM tags WHERE id = ?", [id]);
  });
  notifyChanges(["tags", "note_tags"]);
}

export function upsertTags(tags: Tag[]): void {
  transaction(() => {
    for (const t of tags) {
      execute(
        `INSERT OR REPLACE INTO tags (id, user_id, name, parent_id, color, icon, is_pinned, created_at, sync_status)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'synced')`,
        [
          t.id,
          t.userId ?? "",
          t.name,
          t.parentId ?? null,
          t.color ?? null,
          t.icon ?? null,
          t.isPinned ? 1 : 0,
          t.createdAt ?? new Date().toISOString(),
        ],
      );
    }
  });
  notifyChanges(["tags"]);
}

/** Get tag IDs for a note. */
export function getNoteTagIds(noteId: string): string[] {
  const rows = query<{ tag_id: string }>(
    "SELECT tag_id FROM note_tags WHERE note_id = ?",
    [noteId],
  );
  return rows.map((r) => r.tag_id);
}

/** Set tag IDs for a note (replaces all). */
export function setNoteTags(noteId: string, tagIds: string[]): void {
  transaction(() => {
    execute("DELETE FROM note_tags WHERE note_id = ?", [noteId]);
    for (const tagId of tagIds) {
      execute(
        "INSERT OR IGNORE INTO note_tags (note_id, tag_id) VALUES (?, ?)",
        [noteId, tagId],
      );
    }
  });
  notifyChanges(["note_tags"]);
}

// ── Sync Queue ─────────────────────────────────────────────────────────────

export function hasPendingCreate(entity: string, entityId: string): boolean {
  const row = queryOne<{ cnt: number }>(
    "SELECT COUNT(*) as cnt FROM sync_queue WHERE entity = ? AND entity_id = ? AND type = ?",
    [entity, entityId, "create"],
  );
  return (row?.cnt ?? 0) > 0;
}

export function enqueueSyncOp(op: Omit<SyncOperation, "id">): void {
  execute(
    "INSERT INTO sync_queue (type, entity, entity_id, data, timestamp, retries) VALUES (?, ?, ?, ?, ?, ?)",
    [
      op.type,
      op.entity,
      op.entityId,
      op.data ? JSON.stringify(op.data) : null,
      op.timestamp,
      op.retries,
    ],
  );
  notifyChanges(["sync_queue"]);
  scheduleSyncFlush();
}

export function getAllSyncOps(): SyncOperation[] {
  const rows = query<SyncOpRow>("SELECT * FROM sync_queue ORDER BY id");
  return rows.map(rowToSyncOp);
}

export function deleteSyncOp(id: number): void {
  execute("DELETE FROM sync_queue WHERE id = ?", [id]);
  notifyChanges(["sync_queue"]);
}

export function updateSyncOpRetries(id: number, retries: number): void {
  execute("UPDATE sync_queue SET retries = ? WHERE id = ?", [retries, id]);
}

export function deleteSyncOpsByEntity(entity: string, entityId: string): void {
  execute("DELETE FROM sync_queue WHERE entity = ? AND entity_id = ?", [
    entity,
    entityId,
  ]);
  notifyChanges(["sync_queue"]);
}

// ── Note Links ─────────────────────────────────────────────────────────────

interface TiptapNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TiptapNode[];
}

function extractMentionIds(
  node: TiptapNode,
  ids: Set<string>,
  selfId: string,
): void {
  if (
    node.type === "noteMention" &&
    typeof node.attrs?.id === "string" &&
    node.attrs.id !== selfId
  ) {
    ids.add(node.attrs.id);
  }
  node.content?.forEach((child) => extractMentionIds(child, ids, selfId));
}

/** Extract note IDs from [[id|label]] / [[id#anchor|label]] wiki-link syntax
 *  in markdown content. Heading anchors are stripped: the link target is the
 *  note, so backlinks keep resolving for anchored mentions. */
function extractWikiLinkIds(
  content: string,
  ids: Set<string>,
  selfId: string,
): void {
  const pattern = /\[\[([^|\]]+)\|[^\]]*\]\]/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(content)) !== null) {
    const id = match[1].split("#")[0].trim();
    if (id && id !== selfId) ids.add(id);
  }
}

/** Extract link target IDs from note content (markdown or Tiptap JSON). */
export function extractNoteLinks(noteId: string, content: string): Set<string> {
  const targetIds = new Set<string>();
  try {
    const doc = JSON.parse(content) as TiptapNode;
    extractMentionIds(doc, targetIds, noteId);
  } catch {
    extractWikiLinkIds(content, targetIds, noteId);
  }
  return targetIds;
}

/** Replace note_links rows for a single note. Caller must wrap in a transaction
 *  and call notifyChanges if batching multiple notes. */
export function writeNoteLinks(noteId: string, targetIds: Set<string>): void {
  execute("DELETE FROM note_links WHERE source_note_id = ?", [noteId]);
  for (const targetId of targetIds) {
    execute(
      "INSERT OR IGNORE INTO note_links (source_note_id, target_note_id) VALUES (?, ?)",
      [noteId, targetId],
    );
  }
}

export function syncNoteLinksLocal(noteId: string, content: string): void {
  const targetIds = extractNoteLinks(noteId, content);
  transaction(() => {
    writeNoteLinks(noteId, targetIds);
  });
  notifyChanges(["note_links"]);
}

/** One-time rebuild of note_links from all existing note content.
 *  Needed because the sync pull path didn't populate note_links before. */
function rebuildNoteLinksIfNeeded(): void {
  const done = queryOne<{ value: string }>(
    "SELECT value FROM meta WHERE key = 'note_links_rebuilt'",
  );
  if (done) return;

  const notes = query<{ id: string; content: string }>(
    "SELECT id, content FROM notes WHERE content IS NOT NULL AND content != ''",
  );

  transaction(() => {
    execute("DELETE FROM note_links");
    for (const note of notes) {
      writeNoteLinks(note.id, extractNoteLinks(note.id, note.content));
    }
    execute(
      "INSERT OR REPLACE INTO meta (key, value) VALUES ('note_links_rebuilt', '1')",
    );
  });
  notifyChanges(["note_links"]);
}

export interface NoteLinkRef {
  id: string;
  title: string;
}

interface NoteLinkRefRow {
  id: string;
  title: string;
}

const ACTIVE_NOTE_FILTER = "n.deleted_at IS NULL AND n.archived_at IS NULL";

/** Notes linking to `noteId` (backlinks), excluding trashed and archived sources. */
export function listBacklinks(noteId: string): NoteLinkRef[] {
  const rows = query<NoteLinkRefRow>(
    `SELECT n.id, n.title FROM note_links l
     JOIN notes n ON n.id = l.source_note_id
     WHERE l.target_note_id = ? AND ${ACTIVE_NOTE_FILTER}
     ORDER BY n.updated_at DESC`,
    [noteId],
  );
  // Titles are cleaned of markdown at read time, mirroring rowToLocalNote.
  return rows.map((row) => ({ id: row.id, title: noteTitleForDisplay(row.title) }));
}

/** Notes `noteId` links to (outgoing links), excluding trashed and archived targets. */
export function listOutgoingNoteLinks(noteId: string): NoteLinkRef[] {
  const rows = query<NoteLinkRefRow>(
    `SELECT n.id, n.title FROM note_links l
     JOIN notes n ON n.id = l.target_note_id
     WHERE l.source_note_id = ? AND ${ACTIVE_NOTE_FILTER}
     ORDER BY n.updated_at DESC`,
    [noteId],
  );
  // Titles are cleaned of markdown at read time, mirroring rowToLocalNote.
  return rows.map((row) => ({ id: row.id, title: noteTitleForDisplay(row.title) }));
}

// ── Meta (key-value store) ─────────────────────────────────────────────────

export function getMeta(key: string): string | null {
  const row = queryOne<{ value: string }>(
    "SELECT value FROM meta WHERE key = ?",
    [key],
  );
  return row?.value ?? null;
}

export function setMeta(key: string, value: string): void {
  execute("INSERT OR REPLACE INTO meta (key, value) VALUES (?, ?)", [
    key,
    value,
  ]);
}

export function deleteMeta(key: string): void {
  execute("DELETE FROM meta WHERE key = ?", [key]);
}

// ── Image Upload Queue ─────────────────────────────────────────────────────

export interface ImageUploadQueueItem {
  id: number;
  noteId: string;
  fileName: string;
  contentType: string;
  fileData: string; // base64 string
  /** Legacy path: full `data:` URL originally inserted into the note. Empty for new rows. */
  base64Src: string;
  /** Client-supplied attachment id (same id already baked into markdown). Empty for legacy rows. */
  attachmentId: string;
  /** Canonical HTTPS URL already present in markdown. Empty for legacy rows. */
  finalURL: string;
  status: "pending" | "uploading" | "failed";
  retries: number;
  createdAt: string;
}

interface ImageQueueRow {
  id: number;
  note_id: string;
  file_name: string;
  content_type: string;
  file_data: string;
  base64_src: string;
  attachment_id: string;
  final_url: string;
  status: string;
  retries: number;
  created_at: string;
}

function rowToImageQueueItem(row: ImageQueueRow): ImageUploadQueueItem {
  return {
    id: row.id,
    noteId: row.note_id,
    fileName: row.file_name,
    contentType: row.content_type,
    fileData: row.file_data,
    base64Src: row.base64_src ?? "",
    attachmentId: row.attachment_id ?? "",
    finalURL: row.final_url ?? "",
    status: row.status as "pending" | "uploading" | "failed",
    retries: row.retries,
    createdAt: row.created_at,
  };
}

export function enqueueImageUpload(
  item: Omit<ImageUploadQueueItem, "id" | "status" | "retries" | "createdAt">,
): void {
  execute(
    `INSERT INTO image_upload_queue (note_id, file_name, content_type, file_data, base64_src, attachment_id, final_url, status, retries, created_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, 'pending', 0, datetime('now'))`,
    [
      item.noteId,
      item.fileName,
      item.contentType,
      item.fileData,
      item.base64Src,
      item.attachmentId,
      item.finalURL,
    ],
  );
  notifyChanges(["image_upload_queue"]);
}

export function getPendingImageUploads(): ImageUploadQueueItem[] {
  const rows = query<ImageQueueRow>(
    "SELECT * FROM image_upload_queue WHERE status IN ('pending', 'uploading') AND retries < 3 ORDER BY id",
  );
  return rows.map(rowToImageQueueItem);
}

export function updateImageUploadStatus(
  id: number,
  status: "pending" | "uploading",
): void {
  execute("UPDATE image_upload_queue SET status = ? WHERE id = ?", [status, id]);
}

export function incrementImageUploadRetries(id: number): void {
  execute(
    "UPDATE image_upload_queue SET retries = retries + 1, status = 'pending' WHERE id = ?",
    [id],
  );
}

export function deleteImageUploadItem(id: number): void {
  execute("DELETE FROM image_upload_queue WHERE id = ?", [id]);
  notifyChanges(["image_upload_queue"]);
}

/**
 * Park permanently failed uploads (retries >= 3) instead of deleting them.
 *
 * This row is the ONLY durable copy of an attachment that has never reached
 * the server, which is every attachment added without an account. Deleting it
 * destroyed the bytes and left a URL in the markdown that 404s forever, and
 * three failures in a row is a storage cap or a flaky network, not a reason to
 * lose the user's image. `getPendingImageUploads` filters on status, so a
 * parked row stops being retried; `queuedAttachmentBytes` ignores status, so it
 * still renders.
 */
export function markFailedImageUploads(): void {
  execute(
    "UPDATE image_upload_queue SET status = 'failed' WHERE retries >= 3 AND status <> 'failed'",
  );
  notifyChanges(["image_upload_queue"]);
}

/**
 * Put parked rows back in the queue. Called on sign-in (the local-mode
 * attachments finally have an account to upload to) and after a successful
 * purchase (a storage cap that just moved).
 */
export function retryFailedImageUploads(): void {
  execute(
    "UPDATE image_upload_queue SET status = 'pending', retries = 0 WHERE status = 'failed'",
  );
  notifyChanges(["image_upload_queue"]);
}

/**
 * The bytes queued under a markdown URL, whatever the row's status or retry
 * count: a failed upload still has to render. Feeds the blob-cache rehydrate
 * that survives a page reload (`setLocalAttachmentFallback`).
 */
export function queuedAttachmentBytes(
  finalURL: string,
): { fileData: string; contentType: string } | undefined {
  const rows = query<{ file_data: string; content_type: string }>(
    "SELECT file_data, content_type FROM image_upload_queue WHERE final_url = ? LIMIT 1",
    [finalURL],
  );
  if (rows.length === 0) return undefined;
  return { fileData: rows[0].file_data, contentType: rows[0].content_type };
}

/**
 * The person's role on a note shared with them changed (live `access`):
 * stored at once so the editor turns read-only (or editable) before the pull
 * that carries it lands.
 */
export function setNoteShareRole(id: string, role: "owner" | "editor" | "viewer"): void {
  execute("UPDATE notes SET share_role = ? WHERE id = ? AND owner_id IS NOT NULL", [role, id]);
  notifyChanges(["notes"]);
}

/** The owner changed a note's sharing in the sheet: the list badge follows at once. */
export function setNoteSharingSummary(
  id: string,
  summary: { ownerId: string; memberCount: number; linkAccess: "none" | "view" | "edit" },
): void {
  execute(
    `UPDATE notes SET owner_id = ?, share_role = 'owner', member_count = ?, link_access = ?,
       shared_at = CASE WHEN ? = 'none' THEN NULL ELSE COALESCE(shared_at, ?) END
     WHERE id = ?`,
    [summary.ownerId, summary.memberCount, summary.linkAccess, summary.linkAccess, new Date().toISOString(), id],
  );
  notifyChanges(["notes"]);
}

/**
 * Local text with nowhere to go (no common base, lost access, Leave): saved
 * as a new private note "Copy of …" and queued as a create.
 */
export function createCopyOfNote(noteId: string, text: string): void {
  const source = getNote(noteId);
  const sourceTitle = source?.title || i18next.t("common.untitled");
  const title = i18next.t("conflicts.copyOf", { title: sourceTitle });
  const now = new Date().toISOString();
  const id = crypto.randomUUID();
  transaction(() => {
    putNote({
      id,
      userId: source?.userId ?? "",
      title,
      content: text,
      isPinned: false,
      isEncrypted: false,
      wordCount: text.split(/\s+/).filter(Boolean).length,
      tags: [],
      createdAt: now,
      updatedAt: now,
      syncStatus: "pending",
    });
    enqueueSyncOp({
      type: "create",
      entity: "note",
      entityId: id,
      data: { id, title, content: text, created_at: now, updated_at: now },
      timestamp: Date.now(),
      retries: 0,
    });
  });
  writeNoteLinks(id, extractNoteLinks(id, text));
  notifyChanges(["notes", "note_links"]);
}

/**
 * Leave a note shared with this account (PLAN.md D5: a member's delete means
 * Leave). Local first: the note goes from this device and a `delete` op
 * tells the server, which removes the membership. Its queued ops go too.
 */
export function leaveSharedNote(id: string): void {
  transaction(() => {
    // Text not yet sent stays with the person as "Copy of …", as when access is lost.
    const row = queryOne<{ content: string; base_content: string | null; sync_status: string }>(
      "SELECT content, base_content, sync_status FROM notes WHERE id = ?",
      [id],
    );
    if (row) {
      const s = loadSyncState(id);
      const unsent = s ? hasUnsentWork(s, row.sync_status) : row.sync_status !== "synced" && row.content !== (row.base_content ?? "");
      if (unsent) createCopyOfNote(id, s?.content ?? row.content);
    }
    execute("DELETE FROM sync_queue WHERE entity = 'note' AND entity_id = ?", [id]);
    execute("DELETE FROM sync_queue WHERE entity = 'note_tag' AND entity_id LIKE ?", [`${id}:%`]);
    deleteNoteLocal(id);
    enqueueSyncOp({
      type: "delete",
      entity: "note",
      entityId: id,
      timestamp: Date.now(),
      retries: 0,
    });
  });
  notifyChanges(["notes", "note_tags", "note_links", "sync_queue"]);
}

/** The public link went on or off (sharing off: today's routes). Never touches the sync status. */
export function setNoteSharedAt(id: string, sharedAt: string | null): void {
  execute("UPDATE notes SET shared_at = ? WHERE id = ?", [sharedAt, id]);
  notifyChanges(["notes"]);
}
